import type { IExtensionLiveCallResponse, IExtensionLiveCallState } from '@/openapi-clients-generated/talk';

export interface PartitionDockCallsInput {
    /** The live-call feed for the manager's currently watched extensions (see `extension-live-calls.manager.ts`). */
    calls: IExtensionLiveCallResponse[];
    /** The workstation's linked extension, only when its calls should get desk-phone treatment (shared workstation mode). Null everywhere else — including a dedicated workstation or a browser, where the link is ignored entirely. */
    linkedExtensionId: string | null;
    /** Call ids already shown by the softphone's own incoming/active rows. The user's own softphone UI always wins over a duplicate desk row for the same call. */
    softphoneCallIds: ReadonlySet<string>;
}

export interface PartitionedDockCalls {
    /** Linked-extension calls ringing inbound — their own row, Decline only (no Accept), click is a no-op. */
    deskIncoming: IExtensionLiveCallResponse[];
    /** Linked-extension calls that are connected, held, or an outbound click-to-call ring-back. Each exact call gets its own row. */
    deskActive: IExtensionLiveCallResponse[];
    /** Starred/primary-extension calls (never the linked extension), including read-only observations. */
    starred: IExtensionLiveCallResponse[];
}

/**
 * Splits the extension-live-calls feed for the dock. In practice the manager watches either the
 * linked extension (shared workstation) or the starred/primary set (dedicated workstation / browser)
 * — never both — but this stays defensive about a mixed input: a linked-extension entry wins any
 * callId collision against a starred one.
 */
export function partitionDockCalls({ calls, linkedExtensionId, softphoneCallIds }: PartitionDockCallsInput): PartitionedDockCalls {
    const deskIncoming: IExtensionLiveCallResponse[] = [];
    const deskActive: IExtensionLiveCallResponse[] = [];
    const starred: IExtensionLiveCallResponse[] = [];

    const ordered = linkedExtensionId
        ? [...calls].sort((a, b) => Number(b.extensionId === linkedExtensionId) - Number(a.extensionId === linkedExtensionId))
        : calls;

    const seenCallIds = new Set<string>();
    for (const call of ordered) {
        if (softphoneCallIds.has(call.callId) || seenCallIds.has(call.callId)) continue;
        seenCallIds.add(call.callId);

        if (linkedExtensionId && call.extensionId === linkedExtensionId) {
            if (call.state === 'ringing' && call.direction === 'incoming') deskIncoming.push(call);
            else deskActive.push(call);
        } else {
            starred.push(call);
        }
    }

    return { deskIncoming, deskActive, starred };
}

const LIVE_CALL_STATE_LABELS: Record<IExtensionLiveCallState, string> = {
    ringing: 'Ringing',
    connected: 'On call',
    held: 'On hold'
};

export function describeExtensionLiveCallState(state: IExtensionLiveCallState): string {
    return LIVE_CALL_STATE_LABELS[state];
}

/** `"Ringing · Jane Doe"` — resolve `remoteLabel` first (see `talk/shared/party-names.ts`); this stays pure. */
export function describeExtensionLiveCall(call: Pick<IExtensionLiveCallResponse, 'state'>, remoteLabel: string): string {
    return `${describeExtensionLiveCallState(call.state)} · ${remoteLabel}`;
}

/** Transfers require an answered call, its exact source leg, and server authorization. */
export function canTransferDeskCall(call: IExtensionLiveCallResponse): boolean {
    return (call.state === 'connected' || call.state === 'held') && !!call.controlLegId && call.canBlindTransfer === true;
}
